import { Fragment, MutableRefObject, useEffect, useRef, useState } from 'react'
import {
    latLng,
    IconOptions,
    Icon,
    LeafletMouseEvent,
} from 'leaflet'
import {
    TileLayer,
    Marker,
    Popup,
    SVGOverlay,
    ZoomControl,
    Map as MapType
} from 'react-leaflet'
import { Map as LeafletMap } from 'react-leaflet'
import { getMapIcon } from '../../Services/DataAvailabilityIndicator'
import FetchData from '../../Services/FetchData'
import UUID, { isUUID } from '../../Services/UuidGenerator'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import LocationSearchBar from './LocationSearchBar/LocationSearchBar'
import ReactGA from 'react-ga4'
import Legend from './Legend/Legend'
import { DataLocation, DataStream } from '../../Redux/storeTypes'
import TimeSince from '../../Services/TimeSince'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCrosshairs, faList, faStar } from '@fortawesome/free-solid-svg-icons'
import MapPopup from './MapPopup/MapPopup'
import { mapAttribution } from '../../Assets/ReuseableStringCons'
import './Map.scss'
import VirtualRainGaugeButton from './VirtualRainGaugeButton/VirtualRainGaugeButton'
import VirtualRainGaugePopupMenu from './VirtualRainGaugePopupMenu/VirtualRainGaugePopupMenu'
import AboutVRGModal from './AboutVRGModal/AboutVRGModal'
import locationClickAnalytics from '../../Services/GoogleAnalyticsEvents'
import { useAppDispatch, useAppSelect } from '../../Redux/Store'
import {
    helperZoomVRGToggle,
    setTempGrab,
    setTempVRG,
    showAlert,
    toggleDataShown,
    toggleFeedbackShown,
    toggleImporterShown,
    toggleLocationsShown,
    toggleMobileLocationsShown,
    addVRG,
    addGrab
} from '../../Redux/NewReducers'

const cairnsCenter = latLng(-16.9186, 145.752)

const checkName = (array: DataStream[], searchTerm: string) => {
    return (
        array
            .map((e: DataStream) => {
                return e.name
            })
            .indexOf(searchTerm) !== -1
    )
}

export default function Map() {
    const dispatch = useAppDispatch()
    const locations = useAppSelect(state => state.locations.locations)
    const rainDataHighlight = useAppSelect(state => state.highlight.rainDataHighlight)
    const recentDataUnavailableHighlight = useAppSelect(state => state.highlight.recentDataUnavailableHighlight)
    const nitrateLoadsHighlight = useAppSelect(state => state.highlight.nitrateLoadsHighlight)
    const nitrateConcentrationsHighlight = useAppSelect(state => state.highlight.nitrateConcentrationsHighlight)
    const pesticideHighlight = useAppSelect(state => state.highlight.pesticideHighlight)
    const [VRG_popup_is_shown, setVRGPopupState] = useState(false)
    const [grabPopupIsShown, setGrabPopupState] = useState(false)
    const tempVRG = useAppSelect(state => state.tempVRG.tempVRG)
    const tempGrab = useAppSelect(state => state.tempGrab.tempGrab)
    const clusterHighlight = useAppSelect(state => state.highlight.clusterHighlight)
    const mobileLocationsShown = useAppSelect(state => state.ui.mobileLocationsShown)
    const locationsShown = useAppSelect(state => state.ui.locationsShown)
    const dataShown = useAppSelect(state => state.ui.dataShown)
    const helperZoomVRG = useAppSelect(state => state.ui.helperZoomVRG)
    const email = useAppSelect(state => state.locations.email)
    const mapRef: MutableRefObject<MapType> = useRef(null)
    const [hasRunSetup, setHasRunSetup] = useState(false)
    const locationId = useAppSelect(state => state.ui.locationId)
    const satellite = useAppSelect(state => state.ui.satelliteMap)

    useEffect(() => {
        if (!hasRunSetup) {
            mapRef.current.leafletElement.setZoom(6)
            mapRef.current.leafletElement.invalidateSize()
            setHasRunSetup(true)
        } else {
            if (
                locations.length > 0 &&
                locations[locationId]?.lat &&
                locations[locationId]?.lng
            ) {
                mapRef.current.leafletElement.flyTo(
                    [
                        locations[locationId].lat,
                        locations[locationId].lng,
                    ],
                    14
                )
            }
            mapRef.current.leafletElement.invalidateSize()
        }
    }, [hasRunSetup, locations, locationId, locationsShown, mobileLocationsShown])

    async function popupClicked(locationId: number) {
        dispatch(toggleDataShown({ shouldShow: true, urlId: locations[locationId].id, locationId: locationId, pushHistory: true }))
        ReactGA.event({
            category: 'Data',
            action: 'Sensor clicked from map',
            label: 'Sensor clicked from map',
        })
        locationClickAnalytics(locations[locationId].nodes)
        mapRef.current.leafletElement.flyTo([
            locations[locationId].lat,
            locations[locationId].lng,
        ])
        await FetchData.LoadNodes(locationId)
    }

    function displayIcon(location: DataLocation): Icon<IconOptions> {
        if (location.locationIdentifier === 'VRG') {
            return getMapIcon('VRG', rainDataHighlight)
        }
        if (
            location.nodes.every((data: DataStream) => {
                return (
                    data.latestTime === null ||
                    new Date(data.latestTime) < TimeSince.DaysAgo(2)
                )
            })
        ) {
            if (checkName(location.nodes, 'Nitrate-N loads')) {
                return getMapIcon(
                    'loads-missing',
                    recentDataUnavailableHighlight
                )
            } else if (
                checkName(location.nodes, 'Nitrate-N concentrations')
            ) {
                return getMapIcon(
                    'conc-missing',
                    recentDataUnavailableHighlight
                )
            } else if (checkName(location.nodes, 'Rainfall')) {
                return getMapIcon(
                    'rain-missing',
                    recentDataUnavailableHighlight
                )
            }
        } else {
            if (checkName(location.nodes, 'Nitrate-N loads')) {
                return getMapIcon(
                    'loads-avail',
                    nitrateLoadsHighlight
                )
            } else if (
                checkName(location.nodes, 'Nitrate-N concentrations')
            ) {
                return getMapIcon(
                    'conc-avail',
                    nitrateConcentrationsHighlight
                )
            } else if (checkName(location.nodes, 'Rainfall')) {
                return getMapIcon('rain-avail', rainDataHighlight)
            }
        }
        if (location.nodes.some((node) => node.unit === 'ug/L')) {
            return getMapIcon('pesticide-avail', pesticideHighlight)
        }
        if (location.nodes.length === 0 && isUUID(location.locationIdentifier)) {
            return getMapIcon('pesticide-missing', pesticideHighlight)
        }
        return getMapIcon('no icon', false)
    }

    const centerToMyLocation = () => {
        navigator.geolocation.getCurrentPosition(
            (l) => {
                mapRef.current.leafletElement.flyTo(
                    [l.coords.latitude, l.coords.longitude],
                    10
                )
            },
            () => {
                dispatch(showAlert({ text: 'Location access denied', is_error: true }))
            }
        )
    }

    function mapClickHandler(leafletClick: LeafletMouseEvent) {
        if (
            window.localStorage.getItem(
                FetchData.LOCALSTORAGE_VRG_MODAL_KEY
            ) === 'viewed'
        ) {
            if (VRG_popup_is_shown) {
                if (
                    latLng(
                        leafletClick.latlng.lat,
                        leafletClick.latlng.lng
                    ).distanceTo(cairnsCenter) > 60000
                ) {
                    dispatch(helperZoomVRGToggle(true))
                } else {
                    dispatch(setTempVRG({
                        lat: leafletClick.latlng.lat,
                        lng: leafletClick.latlng.lng,
                    }))
                }
            }
        }

        if (grabPopupIsShown) {
            dispatch(setTempGrab({
                lat: leafletClick.latlng.lat,
                lng: leafletClick.latlng.lng,
            }))
        }
    }

    async function addNewVRG() {
        if (tempVRG) {
            const newVRG = {
                catchments: await FetchData.PointToCatchment(
                    tempVRG.lat,
                    tempVRG.lng
                ),
                lat: tempVRG.lat,
                lng: tempVRG.lng,
                loaded: false,
                loadInProgress: false,
                locationIdentifier: 'VRG',
                name: `VRG ${tempVRG.lat.toFixed(
                    3
                )} ${tempVRG.lng.toFixed(3)}`,
                nodes: [
                    {
                        apiIdentifier: 'VRG',
                        data: [] as any,
                        latestTime: null as any,
                        latestValue: null as any,
                        locationIdentifier: 'VRG',
                        name: 'VRG',
                        nodeIdentifier: `VRG?lat=${tempVRG.lat}&lon=${tempVRG.lng}`,
                        unit: 'mm',
                    },
                ],
                organisation: 'CSIRO',
                id: UUID() // Use UUID as initial id to ensure uniqueness
            }
            dispatch(addVRG(newVRG))
            FetchData.updateStoredVRGs(newVRG, 'add', email, locations)
            mapRef.current.leafletElement.flyTo(
                latLng(tempVRG.lat, tempVRG.lng),
                14
            )
        }
        endAddingVRG()
    }

    function endAddingVRG() {
        dispatch(helperZoomVRGToggle(false))
        setVRGPopupState(false)
        setGrabPopupState(false)
        dispatch(setTempVRG(null))
        dispatch(setTempGrab(null))
    }

    async function addNewGrab() {
        if (tempGrab) {
            const locationId = UUID()
            const newGrab = {
                catchments: await FetchData.PointToCatchment(
                    tempGrab.lat,
                    tempGrab.lng
                ),
                lat: tempGrab.lat,
                lng: tempGrab.lng,
                loaded: false,
                loadInProgress: false,
                locationIdentifier: locationId,
                name: "New Location",
                nodes: [] as any[],
                organisation: 'CSIRO',
                id: UUID() // Use UUID as initial id to ensure uniqueness
            }
            dispatch(addGrab(newGrab))
            FetchData.updateStoredGrabs(newGrab, 'add')
            mapRef.current.leafletElement.flyTo(
                latLng(tempGrab.lat, tempGrab.lng),
                14
            )
        }
        endAddingGrab()
    }

    function endAddingGrab() {
        dispatch(helperZoomVRGToggle(false))
        dispatch(setTempVRG(null))
        dispatch(setTempGrab(null))
        setGrabPopupState(false)
        dispatch(toggleImporterShown(true))
        // TODO select it in grab importer
    }

    function zoomToRadius() {
        dispatch(helperZoomVRGToggle(false))
        mapRef.current.leafletElement.flyTo(cairnsCenter, 10)
    }

    return (
        <LeafletMap
            id="map"
            className={`${clusterHighlight
                ? 'map cluster-highlighted '
                : 'map cluster-not-highlighted '
                }${VRG_popup_is_shown || grabPopupIsShown ? 'dropMarker' : ''}`}
            center={[-19, 145]}
            zoom={5}
            maxZoom={16}
            minZoom={4}
            ref={mapRef}
            style={{
                display: mobileLocationsShown ? 'none' : 'block',
            }}
            zoomControl={false}
            onclick={(event: LeafletMouseEvent) => mapClickHandler(event)}
        >
            <TileLayer
                url={`https://api.mapbox.com/styles/v1/${satellite
                    ? 'mapbox/satellite-v9'
                    : '1622csiro/ck9t95qps09iu1isc0pywte86'
                    }/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
                    }`}
                tileSize={512}
                zoomOffset={-1}
                attribution={mapAttribution}
            />
            <ZoomControl position="bottomright" />
            <div className="leaflet-bottom leaflet-right">
                <div
                    className="button is-small map-box leaflet-control"
                    style={{ width: '34px !important', marginBottom: '100px' }}
                    onClick={() => centerToMyLocation()}
                >
                    <FontAwesomeIcon icon={faCrosshairs} />
                </div>
            </div>
            <div className="leaflet-top leaflet-left">
                <div className="mapControlBox badge_WIP-parent leaflet-control">
                    {mapRef.current && <LocationSearchBar map={mapRef.current.leafletElement} />}
                    <div className="is-hidden-touch">
                        <div
                            className={
                                'button is-primary is-inverted buttonSpacing ' +
                                (locationsShown
                                    ? 'is-invisible'
                                    : 'inlineFlex')
                            }
                            id="locations-button"
                            onClick={() => {
                                dispatch(toggleLocationsShown())
                            }}
                        >
                            <FontAwesomeIcon icon={faList} />
                            &nbsp;&nbsp;
                            <span>Locations</span>
                        </div>
                    </div>
                    <div className="is-hidden-desktop">
                        <div
                            className="button is-primary is-inverted buttonSpacing"
                            onClick={() => {
                                dispatch(toggleMobileLocationsShown())
                            }}
                        >
                            <FontAwesomeIcon icon={faList} />
                            &nbsp;&nbsp;
                            <span>Locations</span>
                        </div>
                    </div>
                    <span className={
                        'badge_WIP-parent ' +
                        (locationsShown
                            ? 'is-invisible'
                            : 'inlineFlex')
                    } style={{ right: '-5px' }}></span>
                </div>
            </div>
            <div className="leaflet-bottom leaflet-left">
                <div
                    className="button is-small map-box leaflet-control"
                    onClick={() => dispatch(toggleFeedbackShown(true))}
                >
                    <FontAwesomeIcon icon={faStar} />
                    &nbsp;&nbsp;
                    <span>Give us feedback!</span>
                </div>
                <div className="leaflet-control">
                    <Legend showPesticides={locations.filter(l => l.nodes.filter(n => n.name === "GrabSample").length > 0).length > 0} />
                </div>
            </div>
            {tempVRG !== null && (
                <Marker
                    position={[
                        tempVRG.lat,
                        tempVRG.lng,
                    ]}
                    opacity={0.8}
                    icon={getMapIcon('VRG', rainDataHighlight)}
                ></Marker>
            )}
            {tempGrab !== null && (
                <Marker
                    position={[
                        tempGrab.lat,
                        tempGrab.lng,
                    ]}
                    opacity={0.8}
                    icon={getMapIcon('pesticide-avail', pesticideHighlight)}
                ></Marker>
            )}
            <MarkerClusterGroup
                showCoverageOnHover={false}
                onClusterclick={() =>
                    ReactGA.event({
                        category: 'Data',
                        action: 'Cluster clicked on map',
                        label: 'Cluster clicked on map',
                    })
                }
            >
                {locations.map((storeLocation, index) => {
                    return (
                        <div key={`location-${index}`}>
                            <Marker
                                position={[
                                    storeLocation.lat,
                                    storeLocation.lng,
                                ]}
                                opacity={0.8}
                                icon={displayIcon(storeLocation)}
                                onClick={() => {
                                    mapRef.current.leafletElement.flyTo(
                                        [
                                            storeLocation.lat,
                                            storeLocation.lng,
                                        ],
                                        mapRef.current.leafletElement.getZoom()
                                    )
                                    popupClicked(index)
                                }}
                            >
                                <Popup
                                    position={[
                                        storeLocation.lat,
                                        storeLocation.lng,
                                    ]}
                                    maxWidth={150}
                                >
                                    {
                                        <MapPopup
                                            location={storeLocation}
                                            id={index}
                                            popupClicked={popupClicked}
                                            checkName={checkName}
                                        />
                                    }
                                </Popup>
                            </Marker>
                        </div>
                    )
                })}
            </MarkerClusterGroup>
            {!dataShown && false /* Temporarily disabling VRG button */ &&
                (VRG_popup_is_shown ? (
                    <Fragment>
                        <VirtualRainGaugePopupMenu
                            cancelAction={() => endAddingVRG()}
                            doneAction={
                                helperZoomVRG
                                    ? () => zoomToRadius()
                                    : () => addNewVRG()
                            }
                            title={
                                helperZoomVRG
                                    ? 'No data available at this location'
                                    : 'New Virtual Rain Gauge'
                            }
                            subtitle={
                                helperZoomVRG
                                    ? 'Coverage is available up to  60km from Cairns Airport'
                                    : 'Please select a location on the map'
                            }
                            doneActionText={
                                helperZoomVRG
                                    ? 'Zoom to coverage'
                                    : 'Done'
                            }
                        />
                        <AboutVRGModal />
                    </Fragment>
                ) : (
                    <VirtualRainGaugeButton
                        id="virtual-rain-gauge-button"
                        pushButtonAction={() =>
                            setVRGPopupState(true)
                        }
                    />
                ))}
            {!dataShown &&
                grabPopupIsShown && (
                    <VirtualRainGaugePopupMenu
                        cancelAction={() => endAddingGrab()}
                        doneAction={
                            helperZoomVRG
                                ? () => zoomToRadius()
                                : () => addNewGrab()
                        }
                        title={
                            helperZoomVRG
                                ? 'No data available at this location'
                                : 'New Grab Location'
                        }
                        subtitle={
                            helperZoomVRG
                                ? 'Coverage is available everywhere'
                                : 'Please select a location on the map'
                        }
                        doneActionText={
                            helperZoomVRG
                                ? 'Zoom to coverage'
                                : 'Done'
                        }
                    />
                )}
            <SVGOverlay
                bounds={cairnsCenter.toBounds(120000)}
                viewBox="0 0 709 709"
            >
                <circle
                    r="353.5"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    cx="353.5"
                    cy="353.5"
                    fill="none"
                />
                <path
                    d="M511.606323,11.7010829 C552.327388,29.9180735 589.388246,55.4931886 620.947529,87.0524715 C651.843085,117.948028 677.003534,154.116184 695.139796,193.829325 L673.529739,204.28447 C656.569829,166.992418 626,127 603.976966,104.023034 C581.953932,81.0460685 539,51 501.459224,33.4540293 L511.606323,11.7010829 Z"
                    fill="#FFFFFF"
                    fillRule="nonzero"
                />
                <path
                    d="M517.250155,33.2860367 L524.734844,26.0115036 L523.056001,25.1656638 L517.327246,31.0667896 L517.223463,31.0145013 L518.538387,22.8895857 L516.798495,22.012988 L515.437004,32.3725297 L517.250155,33.2860367 Z M523.368539,36.4353793 L527.981754,27.7163293 L526.422838,26.8915134 L521.809624,35.6105634 L523.368539,36.4353793 Z M527.054975,38.4514706 L528.854335,35.2048397 L530.564346,36.1525686 L530.457062,40.3369871 L532.220885,41.3145396 L532.266076,36.7908981 C533.499926,36.990159 534.624716,36.3708619 535.25764,35.228861 C536.172232,33.5786398 535.609487,31.9146519 533.797831,30.9105895 L530.2941,28.9687394 L525.512377,37.5965263 L527.054975,38.4514706 Z M531.30547,34.9845629 L529.52371,33.9970691 L531.144127,31.0733077 L532.890013,32.040919 C533.870579,32.5843719 534.160308,33.4717984 533.65662,34.3806159 C533.142991,35.3073705 532.286036,35.5280158 531.30547,34.9845629 Z M537.438098,44.369976 L541.692507,37.149255 L544.313404,38.6934734 L545.066428,37.4154176 L538.31099,33.4351512 L537.557966,34.713207 L540.172975,36.2539553 L535.918566,43.4746763 L537.438098,44.369976 Z M544.934422,49.2421551 C547.048143,50.5679741 549.160353,50.1337157 550.304553,48.3095461 L553.740785,42.8312462 L552.246704,41.8940919 L548.894016,47.2391985 C548.178437,48.3800284 546.990985,48.7165062 545.751708,47.9391768 C544.512431,47.1618473 544.298457,45.9463332 545.014036,44.8055033 L548.366724,39.4603968 L546.872642,38.5232425 L543.43641,44.0015424 C542.29221,45.825712 542.820702,47.9163361 544.934422,49.2421551 Z M551.407371,53.0895404 L553.528953,51.4406277 L556.546862,53.4576238 L555.823407,56.040964 L557.397722,57.0931446 L559.96333,46.9433104 L558.315131,45.8417494 L549.923992,52.0981356 L551.407371,53.0895404 Z M556.961754,52.0987091 L554.625859,50.5375314 L558.030272,47.8795647 L558.126891,47.9441389 L556.961754,52.0987091 Z M563.926222,61.68738 L564.797555,60.453232 L561.100695,57.8431767 L565.918652,51.0190641 L564.477882,50.0018522 L558.788592,58.0601129 L563.926222,61.68738 Z M569.867402,66.0882542 L572.114545,63.1338247 L573.670654,64.3174028 L572.963296,68.4429953 L574.568373,69.6638189 L575.263018,65.1936015 C576.455439,65.5680655 577.657536,65.1167948 578.447967,64.0775756 C579.590162,62.5758766 579.272324,60.8483011 577.62372,59.5943704 L574.43533,57.1692768 L568.46364,65.0205509 L569.867402,66.0882542 Z M574.571899,63.2679939 L572.950499,62.0347551 L574.974169,59.3741362 L576.562923,60.5825447 C577.455237,61.2612398 577.614461,62.1810856 576.985427,63.0081082 C576.343977,63.8514537 575.464213,63.946689 574.571899,63.2679939 Z M576.832903,71.4640036 L579.091898,70.0090236 L581.919761,72.2848179 L580.97085,74.7941207 L582.446025,75.9813054 L585.898525,66.0978988 L584.354118,64.854998 L575.442937,70.3453929 L576.832903,71.4640036 Z M582.453116,70.967883 L580.264318,69.2063925 L583.890294,66.8596683 L583.980829,66.932528 L582.453116,70.967883 Z M585.108503,78.2001126 L591.444737,70.6399729 L590.093028,69.507091 L583.756794,77.0672307 L585.108503,78.2001126 Z M588.22937,80.8951041 L592.701608,75.7794374 L592.789099,75.8559243 L591.960615,84.1570487 L593.108295,85.1603778 L599.60069,77.733912 L598.324346,76.6181019 L593.852108,81.7337687 L593.764617,81.6572817 L594.5931,73.3561574 L593.440274,72.348329 L586.94788,79.7747949 L588.22937,80.8951041 Z M600.576786,92.4075467 C602.486778,94.2182064 604.831209,94.1894636 606.585433,92.3390032 L607.431975,91.4460196 L604.460324,88.6289153 L603.53383,89.6062363 L605.255304,91.2381815 L605.109253,91.3823091 C604.129994,92.3755451 602.741529,92.349753 601.650104,91.3150903 C600.375122,90.1064162 600.535813,88.3371832 602.097213,86.6901246 C603.635098,85.0678711 605.378353,84.7988989 606.62853,85.984058 C607.546319,86.8541152 607.753613,87.9172176 607.226183,88.9902646 L608.501165,90.1989387 C609.655644,88.5240603 609.334025,86.4388926 607.682005,84.8727895 C605.643026,82.9398515 602.950889,83.1664602 600.778097,85.4584514 C598.581791,87.7752477 598.503079,90.4416876 600.576786,92.4075467 Z M606.064854,97.3613632 L608.46984,96.1630042 L611.030952,98.7353101 L609.812469,101.125359 L611.148491,102.467221 L615.664475,93.0222554 L614.265751,91.6174179 L604.806002,96.0970094 L606.064854,97.3613632 Z M611.70557,97.4848402 L609.723241,95.4938464 L613.584792,93.5591001 L613.666786,93.6414527 L611.70557,97.4848402 Z M614.9211,106.819294 C616.629957,108.637375 618.783275,108.752421 620.352304,107.277654 L625.064371,102.848673 L623.856467,101.563563 L619.258963,105.884864 C618.277698,106.807178 617.04374,106.831833 616.041835,105.765889 C615.03993,104.699946 615.140893,103.469878 616.122158,102.547564 L620.719662,98.2262638 L619.511758,96.9411544 L614.79969,101.370136 C613.230661,102.844903 613.212244,105.001213 614.9211,106.819294 Z M622.121541,114.715008 C623.866402,116.685291 626.204412,116.861052 628.113285,115.170575 L629.034456,114.354796 L626.319723,111.289341 L625.311552,112.182167 L626.884194,113.957981 L626.726133,114.088827 C625.66401,114.992906 624.28308,114.846162 623.286016,113.720286 C622.121264,112.405057 622.435589,110.65657 624.134639,109.15191 C625.808101,107.669911 627.568168,107.553945 628.71026,108.843585 C629.5487,109.790345 629.662521,110.867472 629.043548,111.89045 L630.2083,113.205678 C631.504403,111.637828 631.365799,109.53256 629.856607,107.828393 C627.99391,105.725051 625.292268,105.716089 622.927927,107.809923 C620.537999,109.926418 620.227119,112.575842 622.121541,114.715008 Z M630.599214,124.22622 L631.735845,123.273046 L628.76651,119.732205 L630.929776,117.918099 L633.736588,121.265137 L634.810364,120.364673 L632.003551,117.017635 L634.051582,115.300165 L637.020917,118.841006 L638.157548,117.887832 L634.054946,112.995605 L626.496612,119.333993 L630.599214,124.22622 Z M636.649015,132.230443 C638.046244,134.027552 640.133193,134.517763 641.851696,133.476054 L640.785942,132.105287 C639.764817,132.596134 638.625727,132.278166 637.857881,131.290565 C636.825694,129.962972 637.290052,128.277117 639.065574,126.896672 C640.835699,125.520423 642.583997,125.485966 643.611988,126.808163 C644.375638,127.790366 644.349036,129.014645 643.561058,129.939011 L644.626812,131.309778 C646.114556,129.893308 646.197103,127.63841 644.820854,125.868285 C643.104738,123.661025 640.483527,123.508265 637.952464,125.476133 C635.432195,127.43561 634.932899,130.023183 636.649015,132.230443 Z M642.889852,140.523567 C644.587722,142.847296 647.257548,143.106244 649.824137,141.230924 C652.396245,139.351572 652.945801,136.731863 651.251964,134.413654 C649.56216,132.100964 646.891274,131.817391 644.319166,133.696744 C641.752577,135.572063 641.183916,138.188798 642.889852,140.523567 Z M644.12071,139.62422 C643.072145,138.189138 643.590011,136.464611 645.383863,135.153904 C647.188755,133.835131 648.988671,133.899997 650.021105,135.313001 C651.053539,136.726005 650.568364,138.460511 648.763472,139.779284 C646.969619,141.089991 645.157177,141.042743 644.12071,139.62422 Z M649.677419,149.692434 L659.740178,146.921098 L658.676115,145.371347 L650.813652,147.784562 L650.747874,147.688759 L655.812817,141.201107 L654.710061,139.595001 L648.528231,148.018703 L649.677419,149.692434 Z M656.024391,159.285277 L657.268702,158.477712 L654.752968,154.601426 L657.121172,153.064447 L659.499211,156.728569 L660.674712,155.965662 L658.296673,152.301539 L660.538726,150.846433 L663.05446,154.722719 L664.298771,153.915154 L660.82289,148.559457 L652.548511,153.929581 L656.024391,159.285277 Z M658.10897,162.637316 L661.273278,160.696837 L662.295335,162.363489 L660.142364,165.953138 L661.196584,167.672237 L663.46247,163.756738 C664.438436,164.537495 665.722364,164.552034 666.835408,163.869471 C668.443786,162.88315 668.772946,161.157696 667.690137,159.391977 L665.595991,155.977088 L657.186974,161.133833 L658.10897,162.637316 Z M663.515365,161.711545 L662.450424,159.974964 L665.300049,158.22746 L666.343548,159.929077 C666.929623,160.884779 666.745033,161.799872 665.85926,162.343063 C664.956004,162.896976 664.10144,162.667248 663.515365,161.711545 Z M662.655702,170.170689 L665.288273,169.632548 L667.100026,172.777957 L665.306798,174.773294 L666.251912,176.41412 L673.049269,168.45187 L672.059799,166.734038 L661.765179,168.624641 L662.655702,170.170689 Z M668.074096,171.743563 L666.671778,169.308981 L670.901482,168.434666 L670.959486,168.535367 L668.074096,171.743563 Z M668.845671,181.727296 C670.092295,184.045159 672.327721,184.752305 674.573339,183.544537 L675.657015,182.961699 L673.717462,179.355466 L672.531439,179.993349 L673.655019,182.082436 L673.471169,182.173555 C672.23005,182.810022 670.919555,182.350562 670.207198,181.026069 C669.375036,179.47882 670.081887,177.848983 672.080667,176.773972 C674.049346,175.715151 675.789114,176.005829 676.605086,177.522976 C677.204113,178.636754 677.067934,179.711284 676.230899,180.565089 L677.063061,182.112338 C678.684118,180.883431 679.031909,178.802469 677.953661,176.797668 C676.622849,174.323274 673.995234,173.695108 671.213798,175.191057 C668.402261,176.703195 667.492194,179.210759 668.845671,181.727296 Z M674.916057,192.929093 L676.241075,192.262161 L674.163444,188.134455 L676.685252,186.865133 L678.649167,190.766914 L679.900912,190.136864 L677.936997,186.235082 L680.324472,185.033376 L682.402103,189.161081 L683.727121,188.49415 L680.856548,182.791077 L672.045483,187.22602 L674.916057,192.929093 Z"
                    fill="#243780"
                    fillRule="nonzero"
                />
            </SVGOverlay>
        </LeafletMap>
    )
}
